import { useMemo } from 'react';
import type { DrawerProps } from 'antd';
import { useTheme, useThemeUtils } from '@theme/useTheme';
import { useLayoutViewContext } from '@shared/contexts/LayoutViewContext';

export type ResponsiveDrawerVariant = 'details' | 'form' | 'full';

interface UseResponsiveDrawerOptions {
  variant?: ResponsiveDrawerVariant;
}

interface ResponsiveDrawerConfig {
  size: DrawerProps['size'];
  placement: DrawerProps['placement'];
  styles: DrawerProps['styles'];
}

const VARIANT_MAX_WIDTH: Record<ResponsiveDrawerVariant, number> = {
  details: 720,
  form: 960,
  full: Number.POSITIVE_INFINITY,
};

const VARIANT_GUTTER: Record<ResponsiveDrawerVariant, number> = {
  details: 48,
  form: 32,
  full: 24,
};

export function useResponsiveDrawer(options: UseResponsiveDrawerOptions = {}): ResponsiveDrawerConfig {
  const { variant = 'details' } = options;
  const layout = useLayoutViewContext();
  const { theme, isDark } = useTheme();
  const themeUtils = useThemeUtils();
  const { token } = theme;

  const size = useMemo<NonNullable<DrawerProps['size']>>(() => {
    const viewport = layout.viewportWidth;
    if (layout.isMobile) {
      // Ant Design v6: Drawer `width` deprecated in favor of `size` (number | 'default' | 'large')
      // На mobile Drawer должен занимать весь экран.
      return viewport;
    }

    const available = Math.max(320, viewport - layout.sidebarWidth - VARIANT_GUTTER[variant]);

    if (layout.isTablet) {
      return Math.min(Math.round(viewport * 0.95), available);
    }

    const targetMax = VARIANT_MAX_WIDTH[variant];
    return targetMax === Number.POSITIVE_INFINITY ? available : Math.min(targetMax, available);
  }, [layout.isMobile, layout.isTablet, layout.sidebarWidth, layout.viewportWidth, variant]);

  const headerPaddingX = layout.isMobile ? token.paddingMD : token.paddingLG;
  const bodyPaddingX = layout.isMobile ? token.paddingLG : token.paddingXL;
  const bodyPaddingY = layout.isMobile ? token.paddingMD : token.paddingLG * 1.25;

  const styles: DrawerProps['styles'] = useMemo(
    () => ({
      header: {
        padding: `${token.paddingSM}px ${headerPaddingX}px`,
        borderBottom: `1px solid ${token.colorSplit}`,
        backgroundColor: token.colorBgElevated,
      },
      body: {
        padding: `${bodyPaddingY}px ${bodyPaddingX}px`,
        backgroundColor: token.colorBgContainer,
      },
      footer: {
        padding: `${token.paddingSM}px ${bodyPaddingX}px`,
        borderTop: `1px solid ${token.colorSplit}`,
        backgroundColor: token.colorBgElevated,
      },
      mask: {
        backgroundColor: isDark ? themeUtils.alpha('#000000', 0.65) : themeUtils.alpha('#000000', 0.45),
        backdropFilter: 'blur(2px)',
      },
    }),
    [bodyPaddingX, bodyPaddingY, headerPaddingX, isDark, themeUtils, token],
  );

  return {
    size,
    placement: 'right',
    styles,
  };
}

